import type { HTMLAttributes } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { cn } from '@lean-agility/design-system/react';

const dependencyCountBadgeVariants = cva(
  `inline-flex cursor-pointer items-center gap-0.5 rounded-sm px-1 py-0.5 text-[10px] font-medium transition-colors`,
  {
    variants: {
      isActive: {
        true: 'bg-action-primary/20 text-action-primary',
        false: `
          bg-bg-muted text-text-secondary
          hover:bg-bg-subtle hover:text-text-primary
        `,
      },
    },
    defaultVariants: {
      isActive: false,
    },
  },
);

export interface DependencyCountBadgeProps
  extends Omit<HTMLAttributes<HTMLButtonElement>, 'children'>, VariantProps<typeof dependencyCountBadgeVariants> {
  /**
   * Number of incoming dependencies (predecessors - blocked by).
   */
  incomingCount: number;
  /**
   * Number of outgoing dependencies (dependents - this blocks).
   */
  outgoingCount: number;
}

/**
 * DependencyCountBadge - A compact badge showing dependency counts.
 *
 * Displays incoming (blocked by) and outgoing (blocks) dependency counts
 * in a compact format suitable for timeline label columns.
 *
 * Format: ←2 →1 (2 predecessors, 1 dependent)
 *
 * @example
 * ```tsx
 * // Shows "←2 →1" - blocked by 2, blocks 1
 * <DependencyCountBadge
 *   incomingCount={2}
 *   outgoingCount={1}
 *   onClick={handleClick}
 * />
 * ```
 */
export function DependencyCountBadge({
  incomingCount,
  outgoingCount,
  isActive,
  className,
  ...props
}: DependencyCountBadgeProps) {
  // Don't render if no dependencies
  if (incomingCount === 0 && outgoingCount === 0) {
    return null;
  }

  const label = `${incomingCount} predecessor${incomingCount !== 1 ? 's' : ''}, ${outgoingCount} dependent${outgoingCount !== 1 ? 's' : ''}`;

  return (
    <button
      type="button"
      className={cn(dependencyCountBadgeVariants({ isActive }), className)}
      aria-label={label}
      title={label}
      {...props}
    >
      {incomingCount > 0 && (
        <span className="inline-flex items-center gap-px">
          <ArrowLeft className="size-2.5" aria-hidden="true" />
          <span>{incomingCount}</span>
        </span>
      )}
      {outgoingCount > 0 && (
        <span className="inline-flex items-center gap-px">
          <ArrowRight className="size-2.5" aria-hidden="true" />
          <span>{outgoingCount}</span>
        </span>
      )}
    </button>
  );
}
